import React, { FC } from "react";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { NOTIFICATIONS } from "~/constans/notifications";
import { useGetLocationData } from "~/hooks/locationData";
import {
  AnnouncementStatus,
  useAddNotificationMutation,
  useAnnouncementStatusMutation,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

/** Допустимые переходы статусов объявления (из текущего статуса) */
const ALLOWED_TRANSITIONS: Record<string, AnnouncementStatus[]> = {
  [AnnouncementStatus.Draft]: [
    AnnouncementStatus.Draft,
    AnnouncementStatus.Active,
  ],
  [AnnouncementStatus.Active]: [
    AnnouncementStatus.Active,
    AnnouncementStatus.Revoked,
    AnnouncementStatus.Canceled,
  ],
  [AnnouncementStatus.Renewed]: [
    AnnouncementStatus.Renewed,
    AnnouncementStatus.Revoked,
    AnnouncementStatus.Canceled,
  ],
  [AnnouncementStatus.Revoked]: [
    AnnouncementStatus.Revoked,
    AnnouncementStatus.Renewed,
  ],
  [AnnouncementStatus.Canceled]: [AnnouncementStatus.Canceled],
};

type AnnouncementStatusFormData = {
  status: AnnouncementStatus;
  canceledReason: string;
};

interface AnnouncementItem {
  statusNotTable?: AnnouncementStatus;
  canceledReason?: string;
}

interface IProps {
  setCloseModal: () => void;
  refetch: () => void;
  oneAnnouncement?: AnnouncementItem | false;
  formId: string;
}

const AnnouncementStatusForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  oneAnnouncement,
  formId,
}) => {
  const { t } = useTranslation();
  const { currentId } = useGetLocationData();

  const [addNotificationMutation] = useAddNotificationMutation({});

  const currentStatus = oneAnnouncement
    ? oneAnnouncement.statusNotTable
    : undefined;
  const isCanceled = currentStatus === AnnouncementStatus.Canceled;

  const statusOptions = (ALLOWED_TRANSITIONS[currentStatus || ""] || []).map(
    (value) => ({ value, label: t(`announcement.${value}`) }),
  );

  const { handleSubmit, control, watch, errors } =
    useForm<AnnouncementStatusFormData>({
      defaultValues: {
        status: currentStatus,
        canceledReason:
          (oneAnnouncement && oneAnnouncement.canceledReason) || "",
      },
    });

  const watchStatus = watch("status");

  const [changeAnnouncementStatusMutation] = useAnnouncementStatusMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
    onError(error) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: error.message,
        },
      });
    },
  });

  const onSubmit = (data: AnnouncementStatusFormData) => {
    changeAnnouncementStatusMutation({
      variables: {
        id: currentId,
        canceledReason: data.canceledReason ? data.canceledReason : undefined,
        status: data.status,
      },
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="status"
          control={control}
          label={t("announcement.status")}
          options={statusOptions}
          disabled={isCanceled}
        />
        {watchStatus === AnnouncementStatus.Canceled && (
          <Field
            type="textarea"
            name="canceledReason"
            control={control}
            label={t("announcement.canceledReason")}
            disabled={
              !!oneAnnouncement && !!oneAnnouncement.canceledReason
            }
            rules={{
              required: t("validation.required"),
              maxLength: { value: 250, message: t("validation.more250") },
            }}
            error={errors["canceledReason"]?.message}
          />
        )}
      </Flex>
    </form>
  );
};

export default AnnouncementStatusForm;
